import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  PlaneTypes,
} from '@Components/BigBrowse/Model/ModelTypes';
import { Dimensions } from 'react-native';

import type {
  BrandConcessionContentDM,
  MerchandiserProduct,
  ProductImageDesign,
} from '../../common/MultiSectionView.types';
import { ultraSmallColumnWidth } from '../../common/ultraSmallColumnWidth';
import { isUltraSmallScreen } from '../../header/common/getMultiSectionMainHeroSpecs';
import { PRODUCT_SIDE } from '../../header/common/headerShelf/heroSpecsSizes';
import { MERCHANDISER_WIDTH } from './MerchandiserLayoutLandscape';

export const getColumnWidth = (): number =>
  isUltraSmallScreen() ? ultraSmallColumnWidth() : PRODUCT_SIDE;

export const getProductImages = (
  gridCard: GridCard<PlaneTypes.Layout_Merchandiser>,
  contentBoxSize: PlaneSize,
  {
    productImageDesigns,
    contentHeightDesign,
    contentWidthDesign,
  }: {
    productImageDesigns: ProductImageDesign[];
    contentWidthDesign: number;
    contentHeightDesign: number;
  }
): MerchandiserProduct[] => {
  const { dtoData } = gridCard.data!;
  const { template } = dtoData;
  if (!template.includes('Merchandiser')) {
    throw new Error(
      'Attempted to process a non-Merchandiser template as Merchandiser template'
    );
  }

  const size = Number(template[template.length - 1]);
  if (Number.isNaN(size)) {
    throw new Error('Could not determine Merchandiser template length');
  }

  const products = gridCard.data!.dtoData.products.slice(0, size);

  return products.map((productDto, index) =>
    getProductImage({
      productDto,
      index,
      contentBoxSize,
      productImageDesigns,
      contentWidthDesign,
      contentHeightDesign,
    })
  );
};

const getProductImage = ({
  productDto,
  index,
  contentBoxSize,
  productImageDesigns,
  contentWidthDesign,
  contentHeightDesign,
}: {
  // @ts-expect-error
  productDto: BrandConcessionContentDM['header']['products'][0];
  index: number;
  contentBoxSize: PlaneSize;
  productImageDesigns: ProductImageDesign[];
  contentWidthDesign: number;
  contentHeightDesign: number;
}): MerchandiserProduct => {
  const {
    size: s,
    left: l,
    top: t,
    animatedOutDx: dx,
    animatedOutDy: dy,
    radius,
  } = productImageDesigns[index];

  const size = Math.floor((s / contentWidthDesign) * contentBoxSize.width);
  const left = Math.floor((l / contentWidthDesign) * contentBoxSize.width);
  const top = Math.floor((t / contentHeightDesign) * contentBoxSize.height);

  const x = Math.floor(size / 2 + left - contentBoxSize.width / 2);
  const y = -Math.floor(size / 2 + top - contentBoxSize.height / 2);

  return {
    size: { width: size, height: size },
    position: { x, y },
    product: productDto,
    animateOutPosition: {
      x: x + (dx * contentBoxSize.width) / contentWidthDesign,
      y: y + (dy * contentBoxSize.height) / contentHeightDesign,
    },
    // radius is analogue to screen width, and have as a reference the typical 1440 width of Figma design
    radius: (radius / MERCHANDISER_WIDTH) * Dimensions.get('window').width,
  };
};
